/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-02T04:29:21.361Z
 * 소스 파일: src/data/dto/user/user-info-response-dto
 */

import type { UserInfoResponseDto } from "@/data/dto/user/user-info-response-dto";

/**
 * UserInfoResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isUserInfoResponseDto(input: unknown): input is UserInfoResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    (obj.email === undefined || (obj.email == null || typeof obj.email === "string")) &&
    (obj.loginId === undefined || (obj.loginId == null || typeof obj.loginId === "string")) &&
    (obj.fullName === undefined || (obj.fullName == null || typeof obj.fullName === "string")) &&
    (obj.name === undefined || (obj.name == null || typeof obj.name === "string")) &&
    (obj.role === undefined || (obj.role == null || typeof obj.role === "string")) &&
    (obj.hospital === undefined || true) &&
    (obj.isExpiredSn === undefined || (obj.isExpiredSn == null || typeof obj.isExpiredSn === "boolean"))
  );
}

/**
 * UserInfoResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateUserInfoResponseDto(input: unknown): IValidation<UserInfoResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // email (optional)
      if (objObj.email !== undefined) {
        if (!(objObj.email == null || typeof objObj.email === "string")) {
          errors.push({ path: "email", expected: "string | null", value: objObj.email });
        }
      }
      // loginId (optional)
      if (objObj.loginId !== undefined) {
        if (!(objObj.loginId == null || typeof objObj.loginId === "string")) {
          errors.push({ path: "loginId", expected: "string | null", value: objObj.loginId });
        }
      }
      // fullName (optional)
      if (objObj.fullName !== undefined) {
        if (!(objObj.fullName == null || typeof objObj.fullName === "string")) {
          errors.push({ path: "fullName", expected: "string | null", value: objObj.fullName });
        }
      }
      // name (optional)
      if (objObj.name !== undefined) {
        if (!(objObj.name == null || typeof objObj.name === "string")) {
          errors.push({ path: "name", expected: "string | null", value: objObj.name });
        }
      }
      // role (optional)
      if (objObj.role !== undefined) {
        if (!(objObj.role == null || typeof objObj.role === "string")) {
          errors.push({ path: "role", expected: "string | null", value: objObj.role });
        }
      }
      // hospital (optional)
      if (objObj.hospital !== undefined) {
        // Unknown type: runtime validation is not generated.
      }
      // isExpiredSn (optional)
      if (objObj.isExpiredSn !== undefined) {
        if (!(objObj.isExpiredSn == null || typeof objObj.isExpiredSn === "boolean")) {
          errors.push({ path: "isExpiredSn", expected: "boolean | null", value: objObj.isExpiredSn });
        }
      }
    }
  
  if (errors.length === 0 && isUserInfoResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "UserInfoResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * UserInfoResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertUserInfoResponseDto(input: unknown): UserInfoResponseDto {
  const validation = validateUserInfoResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertUserInfoResponseDto] :: 검증 실패: UserInfoResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
